import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import {
  AddReplyDocument,
  CommentRepliesFragmentDoc,
  GetCommentRepliesDocument,
  RemoveReplyDocument,
  ReplyEdge,
} from '~/__generated__/graphql'
import { CacheEdges } from '~/libs/apollo/cursorConnection'
import { snackbarTextState } from '~/recoil/snackbar'
import { Configs } from '../configs'

export const useRepliesMutation = (videoId: string, commentId: string) => {
  const setSnackbarText = useSetRecoilState(snackbarTextState)
  const [addReply] = useMutation(AddReplyDocument)
  const [removeReply] = useMutation(RemoveReplyDocument)
  const { t } = useTranslation()

  const createReply = useCallback(
    async (contents: string, replyId?: string) => {
      try {
        await addReply({
          variables: { videoId, commentId: commentId, replyId, contents },
          refetchQueries: [
            {
              query: GetCommentRepliesDocument,
              variables: { id: commentId, first: Configs.replies.limit },
            },
          ],
          update: (cache) => {
            cache.updateFragment(
              {
                id: `Comment:${commentId}`,
                fragment: CommentRepliesFragmentDoc,
              },
              (data) => {
                return {
                  replies: {
                    totalCount: (data?.replies?.totalCount ?? 0) + 1,
                  },
                }
              },
            )
          },
        })
        setSnackbarText(t('sent'))
      } catch (err) {
        console.error(err)
        setSnackbarText(t('error_occurred'))
      }
    },
    [videoId, commentId, addReply, setSnackbarText, t],
  )

  const deleteReply = useCallback(
    async (replyId: string) => {
      try {
        await removeReply({
          variables: { id: replyId },
          update: (cache, result, options) => {
            // 削除したリプライをキャッシュから消す
            const reply = result.data?.removeReply?.reply
            if (!!reply) {
              cache.modify({
                id: `CommentRepliesConnection:${commentId}`,
                fields: {
                  edges(value) {
                    if (!!value) {
                      const existing = value as CacheEdges<ReplyEdge>
                      const nodes = Object.assign({}, existing.nodes)
                      const cursors = Object.assign([], existing.cursors)
                      const ids = Object.assign([], existing.ids)
                      const index = ids.indexOf(reply.id)
                      if (index > -1) {
                        const removeCursors = cursors.splice(index, 1)
                        ids.splice(index, 1)
                        delete nodes[removeCursors[0]]
                        cache.evict({ id: cache.identify(reply) })
                        cache.gc()
                        return { nodes, cursors, ids }
                      }
                    }
                    return value
                  },
                },
              })
            }
          },
        })
        setSnackbarText(t('deleted'))
      } catch (err) {
        console.error(err)
        setSnackbarText(t('error_occurred'))
      }
    },
    [setSnackbarText, removeReply, commentId, t],
  )

  return {
    createReply,
    deleteReply,
  }
}
